import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Timer, Camera, MessageSquare, Receipt as ReceiptIcon, Play, Pause, CheckCircle2 } from 'lucide-react';
import {
  fetchJobDetail,
  fetchWorkerRoster,
  startWorkSession,
  pauseWorkSession,
  completeWorkSession,
  saveChecklistItem,
  addCrewNote,
  uploadJobPhoto,
  recordJobExpense,
  type ChecklistItem,
} from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge, type BadgeTone } from '../components/ui/Badge';
import { UploadField } from '../components/ui/UploadField';
import { FieldLeadAssignmentPanel } from './FieldLeadAssignmentPanel';
import { BillingPanel } from './BillingPanel';
import { ContractPanel } from './ContractPanel';
import { ChangeOrderPanel } from './ChangeOrderPanel';
import { FollowUpPanel } from './FollowUpPanel';
import type { ChangeOrder } from '../lib/bridge';
import { useAuth } from '../auth/useAuth';

// The full Field Lead job workflow from
// docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §9.3, as one page rather than
// one route per micro-step: Prepare Job (job info/schedule), Build Today's
// Crew, Start/Pause/Complete Work, Photos/Receipts/Notes, and a read-only
// Customer/Payment snapshot. Every mutation refetches jobDetail so the
// screen reflects exactly what the server just did, matching the
// immediate-feedback standard (§5.1).
//
// Presented as a tabbed operational workspace rather than one long stack of
// bordered boxes. Every tab's content stays mounted at all times (toggled
// with `hidden`, not conditionally rendered) so switching tabs never
// remounts a panel, resets its in-flight query, or drops component state —
// this is a pure layout/visual change over the original single-column
// version, not a behavior change.

const CHECKLIST_STATUSES = ['Not Started', 'In Progress', 'Complete', 'Incomplete - Return Needed', 'Not Completed - Final'];

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}

const MANAGEMENT_ROLES = new Set(['OWNER', 'ADMIN', 'JOB_MANAGER']);
const CONTRACT_CREATE_ROLES = new Set(['OWNER', 'ADMIN', 'JOB_MANAGER', 'ESTIMATOR']);
const CHANGE_ORDER_ROLES = new Set(['OWNER', 'ADMIN', 'JOB_MANAGER', 'FIELD_LEAD']);

function stageTone(stage: string): BadgeTone {
  const s = stage.toLowerCase();
  if (s.includes('cancel') || s.includes('lost')) return 'danger';
  if (s.includes('complete') || s.includes('paid') || s.includes('booked')) return 'success';
  if (s.includes('estimate') || s.includes('pending')) return 'gold';
  return 'neutral';
}

type TabKey = 'overview' | 'work' | 'media' | 'notes' | 'billing';

export function JobDetailPage() {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const { user } = useAuth();
  const location = useLocation();
  const backTo = location.pathname.startsWith('/jobs/') ? '/jobs' : location.pathname.startsWith('/estimates/') ? '/estimates' : '/field';
  const backLabel = backTo === '/jobs' ? 'Jobs' : backTo === '/estimates' ? 'Estimates' : 'Assigned Jobs';
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['jobDetail', jobId], queryFn: () => fetchJobDetail(jobId), enabled: !!jobId });
  const rosterQuery = useQuery({ queryKey: ['workerRoster'], queryFn: fetchWorkerRoster });
  const [tab, setTab] = useState<TabKey>('overview');

  const [selectedRoster, setSelectedRoster] = useState<string[]>([]);
  const [guestName, setGuestName] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('General');
  const [expenseCategory, setExpenseCategory] = useState('Materials');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseVendor, setExpenseVendor] = useState('');
  const [expenseReceiptFile, setExpenseReceiptFile] = useState<File | null>(null);
  const [reasonDrafts, setReasonDrafts] = useState<Record<string, string>>({});

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['jobDetail', jobId] });
  }

  const startMutation = useMutation({
    mutationFn: () => {
      const names = [...selectedRoster, ...(guestName.trim() ? [guestName.trim()] : [])];
      return startWorkSession({ jobId, crewSize: names.length, workerNames: names });
    },
    onSuccess: (result) => {
      if (result.ok) {
        setSelectedRoster([]);
        setGuestName('');
        invalidate();
      }
    },
  });

  const pauseMutation = useMutation({
    mutationFn: () => pauseWorkSession({ jobId }),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const completeMutation = useMutation({
    mutationFn: () => completeWorkSession({ jobId }),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const checklistMutation = useMutation({
    mutationFn: (input: { checklistId: string; status: string; reason?: string }) =>
      saveChecklistItem({ jobId, ...input }),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const noteMutation = useMutation({
    mutationFn: () => addCrewNote({ jobId, note: noteText, noteType }),
    onSuccess: (result) => {
      if (result.ok) {
        setNoteText('');
        invalidate();
      }
    },
  });

  const expenseMutation = useMutation({
    mutationFn: async () => {
      if (!expenseReceiptFile) throw new Error('A receipt photo is required.');
      const receiptDataUrl = await fileToDataUrl(expenseReceiptFile);
      return recordJobExpense({
        jobId,
        category: expenseCategory,
        amount: Number(expenseAmount || 0),
        vendor: expenseVendor,
        receiptDataUrl,
      });
    },
    onSuccess: (result) => {
      if (result.ok) {
        setExpenseAmount('');
        setExpenseVendor('');
        setExpenseReceiptFile(null);
        invalidate();
      }
    },
  });

  if (query.isLoading) return <LoadingState label="Loading job…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load this job.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok || !query.data.job) {
    return <ErrorState message={query.data?.message || 'This job could not be found.'} onRetry={() => query.refetch()} />;
  }

  const { job, operations, commercial } = query.data;
  const ops = operations!;
  const activeSession = ops.workSessions.find((s) => s.status === 'Running' && s.active);
  const latestSchedule = ops.schedule[0] as Record<string, unknown> | undefined;
  const stage = (job.stage as string) || (job.estimateStatus as string) || '—';

  const showFollowUp = user?.role === 'FIELD_LEAD';
  const showBillingTab = !!commercial || (user && CONTRACT_CREATE_ROLES.has(user.role)) || (user && CHANGE_ORDER_ROLES.has(user.role));

  const tabs: { key: TabKey; label: string; icon: typeof ClipboardList }[] = [
    { key: 'overview', label: 'Overview', icon: ClipboardList },
    { key: 'work', label: 'Work Session', icon: Timer },
    { key: 'media', label: 'Photos & Receipts', icon: Camera },
    { key: 'notes', label: 'Notes', icon: MessageSquare },
    ...(showBillingTab ? [{ key: 'billing' as TabKey, label: 'Billing & Contract', icon: ReceiptIcon }] : []),
  ];

  return (
    <div className="space-y-5 pb-10">
      <div>
        <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-300 hover:text-gold-200">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {backLabel}
        </Link>
      </div>

      <Card raised className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-extrabold tracking-tight text-white sm:text-2xl">{(job.customer as string) || job.jobId}</h1>
          <p className="mt-1 truncate text-sm text-slate-400">{(job.address as string) || (job.project as string) || job.jobId}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Badge tone={stageTone(stage)}>{stage}</Badge>
          {user && CONTRACT_CREATE_ROLES.has(user.role) && job.stage === 'Estimate' && !job.contractNo && (
            <Link
              to={`/estimates/builder?documentNo=${encodeURIComponent((job.documentNo as string) || '')}`}
              className="text-xs font-semibold text-gold-300 hover:text-gold-200"
            >
              Edit Estimate
            </Link>
          )}
        </div>
      </Card>

      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" role="tablist" aria-label="Job sections">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-bold transition ${
              tab === t.key
                ? 'border-gold-700 bg-gold-900/40 text-gold-200'
                : 'border-ink-border bg-ink-surface text-slate-400 hover:border-slate-600 hover:text-slate-200'
            }`}
          >
            <t.icon className="h-3.5 w-3.5" aria-hidden="true" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      <div className={tab === 'overview' ? 'space-y-5' : 'hidden'}>
        <Card>
          <CardHeader title="Prepare Job" />
          <p className="text-sm text-slate-300">{(job.project as string) || 'No scope description on file.'}</p>
          {latestSchedule && (
            <p className="mt-2 text-sm text-slate-400">
              Next: {String(latestSchedule.serviceDate ?? '—')} {String(latestSchedule.startTime ?? '')}–{String(latestSchedule.endTime ?? '')}
            </p>
          )}
          <p className="mt-2 text-sm text-slate-400">Checklist: {ops.checklistProgress.text}</p>
        </Card>

        {user && MANAGEMENT_ROLES.has(user.role) && <FieldLeadAssignmentPanel jobId={jobId} />}
        {showFollowUp && <FollowUpPanel jobId={jobId} reviewEligible={!!(commercial as Record<string, unknown> | undefined)?.reviewEligible} />}
      </div>

      {/* Work Session */}
      <div className={tab === 'work' ? 'space-y-5' : 'hidden'}>
        {!activeSession ? (
          <Card>
            <CardHeader title="Build Today's Crew & Start Work" />
            <div className="space-y-4">
              {rosterQuery.isLoading && <LoadingState label="Loading worker roster…" />}
              {rosterQuery.data?.ok && (rosterQuery.data.workers?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-2">
                  {rosterQuery.data.workers!.map((w) => {
                    const checked = selectedRoster.includes(w.name);
                    return (
                      <label
                        key={w.workerId}
                        className={`cursor-pointer rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                          checked ? 'border-gold-700 bg-gold-900/40 text-gold-200' : 'border-ink-border bg-ink text-slate-300 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={() =>
                            setSelectedRoster((prev) => (checked ? prev.filter((n) => n !== w.name) : prev.length < 3 ? [...prev, w.name] : prev))
                          }
                        />
                        {w.name}
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="max-w-xs space-y-1.5">
                <label htmlFor="guest-helper" className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  One-time guest helper (optional)
                </label>
                <input
                  id="guest-helper"
                  className="w-full rounded-lg border border-ink-border bg-ink px-3 py-2 text-sm text-white outline-none focus:border-gold-700 focus:ring-2 focus:ring-gold-900/60"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Name"
                />
              </div>
              <Button
                busy={startMutation.isPending}
                busyLabel="Starting…"
                disabled={selectedRoster.length + (guestName.trim() ? 1 : 0) === 0}
                onClick={() => startMutation.mutate()}
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                Start Work
              </Button>
              {startMutation.data && !startMutation.data.ok && (
                <ErrorState message={String(startMutation.data.message || 'Could not start the job timer.')} />
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-white sm:text-base">Perform Work</h2>
              <span className="rounded-full border border-gold-700 bg-gold-900/30 px-2.5 py-1 text-xs font-semibold text-gold-200">
                {ops.workSummary.elapsedText} elapsed · {activeSession.crewNames}
              </span>
            </div>

            <ul className="space-y-3">
              {ops.checklist.map((item: ChecklistItem) => (
                <li key={item.checklistId} className="rounded-xl border border-ink-border bg-ink p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-white">{item.description}</p>
                    <span className="shrink-0 text-xs text-slate-500">
                      {item.photoCount} photo{item.photoCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <select
                      className="rounded-md border border-ink-border bg-ink-raised px-2 py-1.5 text-xs text-white outline-none focus:border-gold-700"
                      value={item.status}
                      onChange={(e) => {
                        const status = e.target.value;
                        if ((status === 'Incomplete - Return Needed' || status === 'Not Completed - Final') && !reasonDrafts[item.checklistId]) {
                          setReasonDrafts((prev) => ({ ...prev, [item.checklistId]: '' }));
                          return;
                        }
                        checklistMutation.mutate({ checklistId: item.checklistId, status });
                      }}
                    >
                      {CHECKLIST_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <label className="cursor-pointer rounded-md border border-ink-border bg-ink-raised px-2.5 py-1.5 text-xs font-semibold text-slate-200 hover:border-gold-700 hover:text-gold-200">
                      Add Photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          const dataUrl = await fileToDataUrl(file);
                          await uploadJobPhoto({ jobId, dataUrl, checklistId: item.checklistId });
                          invalidate();
                        }}
                      />
                    </label>
                  </div>
                  {(item.status === 'Incomplete - Return Needed' || item.status === 'Not Completed - Final' || item.checklistId in reasonDrafts) && (
                    <div className="mt-2.5 flex items-end gap-2">
                      <input
                        className="flex-1 rounded-md border border-ink-border bg-ink-raised px-2 py-1.5 text-sm text-white outline-none focus:border-gold-700"
                        placeholder="Reason (required)"
                        value={reasonDrafts[item.checklistId] ?? item.reason ?? ''}
                        onChange={(e) => setReasonDrafts((prev) => ({ ...prev, [item.checklistId]: e.target.value }))}
                      />
                      <Button
                        variant="secondary"
                        busy={checklistMutation.isPending}
                        onClick={() =>
                          checklistMutation.mutate({
                            checklistId: item.checklistId,
                            status:
                              item.status === 'Complete' || item.status === 'Not Started' || item.status === 'In Progress'
                                ? 'Incomplete - Return Needed'
                                : item.status,
                            reason: reasonDrafts[item.checklistId] ?? '',
                          })
                        }
                      >
                        Save reason
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {checklistMutation.data && !checklistMutation.data.ok && (
              <ErrorState message={String(checklistMutation.data.message || 'Could not update the checklist item.')} />
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="secondary" busy={pauseMutation.isPending} busyLabel="Ending day…" onClick={() => pauseMutation.mutate()}>
                <Pause className="h-4 w-4" aria-hidden="true" />
                Pause / End Day
              </Button>
              <Button busy={completeMutation.isPending} busyLabel="Completing…" onClick={() => completeMutation.mutate()}>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Complete Job
              </Button>
            </div>
            {pauseMutation.data && !pauseMutation.data.ok && <ErrorState message={String(pauseMutation.data.message || 'Could not pause the job.')} />}
            {completeMutation.data && !completeMutation.data.ok && (
              <ErrorState message={String(completeMutation.data.message || 'Could not complete the job.')} />
            )}
          </Card>
        )}
      </div>

      {/* Photos & Receipts */}
      <div className={tab === 'media' ? 'space-y-5' : 'hidden'}>
        <Card>
          <CardHeader title="Photos" />
          <UploadField
            label="Job photos"
            uploadFile={async (file, onProgress) => {
              onProgress(20);
              const dataUrl = await fileToDataUrl(file);
              onProgress(70);
              const res = await uploadJobPhoto({ jobId, dataUrl, category: 'Other' });
              if (!res.ok) throw new Error(res.message || 'Upload failed.');
              onProgress(100);
              invalidate();
              return { url: res.photoId || '' };
            }}
          />
          {ops.photos.length > 0 && (
            <ul className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {ops.photos.map((p) => (
                <li key={String(p.photoId)} className="overflow-hidden rounded-lg border border-ink-border">
                  <img src={String(p.previewUrl ?? p.fileUrl)} alt={String(p.caption ?? p.category ?? '')} className="h-20 w-full object-cover" />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="Receipts / Job Expenses" />
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              expenseMutation.mutate();
            }}
          >
            <select
              className="rounded-md border border-ink-border bg-ink px-2 py-2 text-sm text-white outline-none focus:border-gold-700"
              value={expenseCategory}
              onChange={(e) => setExpenseCategory(e.target.value)}
            >
              {['Materials', 'Labor / Wages', 'Equipment Rental', 'Dump / Disposal'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              className="w-24 rounded-md border border-ink-border bg-ink px-2 py-2 text-sm text-white outline-none focus:border-gold-700"
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount"
              value={expenseAmount}
              onChange={(e) => setExpenseAmount(e.target.value)}
              required
            />
            <input
              className="w-32 rounded-md border border-ink-border bg-ink px-2 py-2 text-sm text-white outline-none focus:border-gold-700"
              placeholder="Vendor"
              value={expenseVendor}
              onChange={(e) => setExpenseVendor(e.target.value)}
            />
            <label className="cursor-pointer rounded-md border border-ink-border bg-ink-raised px-3 py-2 text-sm font-semibold text-slate-200 hover:border-gold-700 hover:text-gold-200">
              {expenseReceiptFile ? 'Receipt selected' : 'Receipt photo'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => setExpenseReceiptFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <Button type="submit" busy={expenseMutation.isPending} busyLabel="Saving…">
              Add Expense
            </Button>
          </form>
          {expenseMutation.isError && (
            <ErrorState message={expenseMutation.error instanceof Error ? expenseMutation.error.message : 'Could not save this expense.'} />
          )}
          {expenseMutation.data && !expenseMutation.data.ok && (
            <ErrorState message={String(expenseMutation.data.message || 'Could not save this expense.')} />
          )}
          {ops.expenses.length > 0 ? (
            <ul className="mt-3 divide-y divide-ink-border">
              {ops.expenses.map((exp) => (
                <li key={String(exp.expenseId)} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="text-slate-300">
                    {String(exp.category)} — {String(exp.vendor || '—')}
                  </span>
                  <span className="font-semibold text-gold-200">{String(exp.amountText)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState className="mt-3">No expenses recorded yet.</EmptyState>
          )}
        </Card>
      </div>

      {/* Notes */}
      <div className={tab === 'notes' ? 'space-y-5' : 'hidden'}>
        <Card>
          <CardHeader title="Notes" />
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (!noteText.trim()) return;
              noteMutation.mutate();
            }}
          >
            <select
              className="rounded-md border border-ink-border bg-ink px-2 py-2 text-sm text-white outline-none focus:border-gold-700"
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
            >
              {['General', 'Issue', 'Customer Request', 'Safety'].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              className="flex-1 rounded-md border border-ink-border bg-ink px-2 py-2 text-sm text-white outline-none focus:border-gold-700"
              placeholder="Add a note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <Button type="submit" busy={noteMutation.isPending} busyLabel="Saving…">
              Add Note
            </Button>
          </form>
          {noteMutation.data && !noteMutation.data.ok && <ErrorState message={String(noteMutation.data.message || 'Could not save this note.')} />}
          {ops.notes.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {ops.notes.map((n) => (
                <li key={String(n.noteId)} className="rounded-lg border border-ink-border bg-ink p-3">
                  <p className="text-sm text-slate-200">{String(n.note)}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {String(n.noteType)} · {String(n.enteredBy)} · {String(n.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState className="mt-3">No notes yet.</EmptyState>
          )}
        </Card>
      </div>

      {/* Billing & Contract — invoice/payment writes are Owner/Admin/
          Manager-only; Field Lead sees the same read-only summary as
          before (BUSINESS_RULES.md §10). */}
      {showBillingTab && (
        <div className={tab === 'billing' ? 'space-y-5' : 'hidden'}>
          {commercial && (
            <BillingPanel
              documentNo={(job.documentNo as string) || ''}
              stage={job.stage as string | undefined}
              totalText={String((commercial as Record<string, unknown>).currentTotalText ?? '—')}
              canWrite={!!user && MANAGEMENT_ROLES.has(user.role)}
            />
          )}

          {user && CONTRACT_CREATE_ROLES.has(user.role) && (
            <ContractPanel
              documentNo={(job.documentNo as string) || ''}
              contractNo={job.contractNo as string | undefined}
              contractStatus={job.contractStatus as string | undefined}
              booked={String(job.estimateStatus ?? '').toLowerCase() === 'booked'}
            />
          )}

          {user && CHANGE_ORDER_ROLES.has(user.role) && (
            <ChangeOrderPanel
              jobId={jobId}
              changeOrders={((commercial as Record<string, unknown> | undefined)?.changeOrders as ChangeOrder[] | undefined) ?? []}
              canManage={MANAGEMENT_ROLES.has(user.role)}
            />
          )}
        </div>
      )}
    </div>
  );
}
